/**
 * TemplateManagementModal Component
 * Modal for managing trade templates (view, edit, delete)
 */

import { toastError } from "../../../../utils/showApiErrorToast";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Text,
  HStack,
  VStack,
  IconButton,
  useColorModeValue,
  Badge,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useUser } from "@clerk/clerk-react";
// Types
import { TradeTemplate } from "../../../../types/tradeTemplate";
// Services
import { getTemplates, deleteTemplate } from "../../../../services/tradeTemplateService";
// Icons
import { AiOutlineEdit, AiOutlineDelete, AiOutlineCopy } from "react-icons/ai";

interface TemplateManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate?: (template: TradeTemplate) => void;
  onEditTemplate?: (template: TradeTemplate) => void;
}

const TemplateManagementModal: React.FC<TemplateManagementModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  onEditTemplate,
}) => {
  const { user } = useUser();
  const [templates, setTemplates] = useState<TradeTemplate[]>([]);
  const [templateToDelete, setTemplateToDelete] = useState<TradeTemplate | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("#575757", "white");

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen && user?.id) {
      loadTemplates();
    }
  }, [isOpen, user?.id]);

  const loadTemplates = () => {
    if (!user?.id) return;
    const userTemplates = getTemplates(user.id);
    setTemplates(userTemplates);
  };

  const handleDelete = (template: TradeTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!templateToDelete || !user?.id) return;

    try {
      const success = deleteTemplate(user.id, templateToDelete.id);
      if (success) {
        toast.success("Template deleted", {
          description: `Template "${templateToDelete.name}" has been deleted.`,
        });
        loadTemplates();
      } else {
        toastError("Error deleting template", {
          description: "Template not found or could not be deleted.",
        });
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toastError("Error deleting template", {
        description: "An error occurred while deleting the template.",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleSelect = (template: TradeTemplate) => {
    if (onSelectTemplate) {
      onSelectTemplate(template);
    }
    onClose();
  };

  const handleEdit = (template: TradeTemplate) => {
    if (onEditTemplate) {
      onEditTemplate(template);
    }
    onClose();
  };

  const formatDate = (date: Date | string): string => {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg={bgColor}>
          <ModalHeader color="#38BBD1">Manage Templates</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {templates.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color={textColor} fontSize="lg" mb={2}>
                  No templates saved
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Save a trade as a template to quickly log similar trades
                </Text>
              </Box>
            ) : (
              <VStack spacing={3} align="stretch">
                {templates.map((template) => (
                  <Box
                    key={template.id}
                    p={4}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    bg={bgColor}
                  >
                    <Flex justify="space-between" align="start" mb={2}>
                      <VStack align="start" spacing={1} flex="1">
                        <Text fontWeight="bold" color={textColor} fontSize="md">
                          {template.name}
                        </Text>
                        {template.description && (
                          <Text fontSize="sm" color="gray.400">
                            {template.description}
                          </Text>
                        )}
                        <HStack spacing={2} mt={2}>
                          <Badge colorScheme="teal" fontSize="xs">
                            {template.formData.symbol}
                          </Badge>
                          <Badge colorScheme={template.formData.type === "buy" ? "green" : "red"} fontSize="xs">
                            {template.formData.type.toUpperCase()}
                          </Badge>
                          {template.formData.strategy && (
                            <Badge colorScheme="purple" fontSize="xs">
                              {template.formData.strategy}
                            </Badge>
                          )}
                        </HStack>
                        <HStack spacing={4} mt={2}>
                          <Text fontSize="xs" color="gray.400">
                            Created: {formatDate(template.createdAt)}
                          </Text>
                          {template.useCount && template.useCount > 0 && (
                            <Text fontSize="xs" color="gray.400">
                              Used {template.useCount} time{template.useCount !== 1 ? "s" : ""}
                            </Text>
                          )}
                        </HStack>
                      </VStack>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<AiOutlineCopy />}
                          variant="ghost"
                          size="sm"
                          aria-label="Template actions"
                        />
                        <MenuList>
                          <MenuItem onClick={() => handleSelect(template)}>
                            Use Template
                          </MenuItem>
                          {onEditTemplate && (
                            <MenuItem onClick={() => handleEdit(template)}>
                              Edit Template
                            </MenuItem>
                          )}
                          <MenuItem
                            color="red.500"
                            onClick={() => handleDelete(template)}
                          >
                            Delete Template
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.500">
              Delete Template
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete the template{" "}
              <Text as="span" fontWeight="bold">
                "{templateToDelete?.name}"
              </Text>
              ? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setTemplateToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default TemplateManagementModal;

